import {
    Card,
    CardHeader,
    Avatar,
    Tooltip,
    IconButton,
    Typography,
    CardContent,
    Stack,
    Chip,
    Box,
    Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import BusinessIcon from "@mui/icons-material/Business";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SendIcon from "@mui/icons-material/Send";
import PublicIcon from "@mui/icons-material/Public";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import { MarketplaceCompany } from "./marketPlaceApi";
import { QUICK_TAG_LABEL } from "../administration/companyProfile/enums/QuickTagEnum";

type Props = {
    company: MarketplaceCompany;
    isFavorite: boolean;
    toggleFavorite: (id: number) => void;
    onOpen: (company: MarketplaceCompany) => void;
};

export function MarketPlaceCompanyCard({ company, isFavorite, toggleFavorite, onOpen }: Props) {

    return (
        <Card
            variant="outlined"
            sx={{
                width: 1,
                maxWidth: "100%",
                minWidth: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: (t) => t.transitions.create(["border-color", "box-shadow"], { duration: t.transitions.duration.short }),
                "&:hover": {
                    borderColor: (t) => t.palette.success.main,
                    boxShadow: 2,
                },
            }}
        >
            <CardHeader
                sx={{
                    bgcolor: (t) => t.palette.grey[100],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 1.25,
                }}
                avatar={
                    <Avatar >
                        <BusinessIcon />
                    </Avatar>
                }
                action={
                    <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
                        <IconButton
                            onClick={() => toggleFavorite(company.id)}
                            aria-label={isFavorite ? "remove from favorites" : "add to favorites"}
                        >
                            {isFavorite ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
                        </IconButton>
                    </Tooltip>
                }
                title={
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {company.name}
                    </Typography>
                }
                subheader={
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                        <FlagOutlinedIcon sx={{ fontSize: 16, opacity: 0.75 }} />
                        <Typography variant="caption" color="text.secondary">
                            {/* Country / City (CODE) */}
                            {company.country}
                            {company.city ? ` / ${company.city}` : ""}
                            {company.countryCode ? ` (${company.countryCode})` : ""}
                        </Typography>
                    </Stack>
                }
            />

            <CardContent sx={{ flexGrow: 1, pt: 1.25 }}>
                <Box
                    sx={{
                        width: "100%",
                        height: 100,                 // stała wysokość „pola” na logo
                        borderColor: "divider",
                        borderRadius: 2,
                        bgcolor: "background.default",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        p: 1,
                    }}
                >
                    {company?.logoUrl ? (
                        <Box
                            component="img"
                            src={company.logoUrl}
                            alt={company?.name ? `${company.name} logo` : "Company logo"}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                display: "block",
                            }}
                        />
                    ) : (
                        <BusinessIcon sx={{ fontSize: 48, color: "text.disabled" }} />
                    )}
                </Box>


                <Divider sx={{ my: 1.5 }} />
                {!!company.description && (
                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.primary",
                            mb: 1.5,
                            px: 0.5,
                            lineHeight: 1.75,
                            fontWeight: 500,       
                            fontSize: { xs: "1rem", md: "1.1rem" }, // trochę większy rozmiar
                        }}
                    >
                        {company.description}
                    </Typography>
                )}


            </CardContent>

            {/* kreska nad stopką */}
            <Divider sx={{ mt: "auto" }} />

            {/* Dolny pasek: niebieskie tło (info), najpierw TAGI, potem Devices + ikonki */}
            <Box
                sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: (t) => alpha(t.palette.info.main, 0.12), // delikatny niebieski z palety info
                    borderTop: "1px solid",
                    borderColor: "divider",
                }}
            >

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        {company.quickTags.length > 0 ? company.quickTags.map(tag => QUICK_TAG_LABEL[tag]).join(", ") : "-"}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Open details">
                            <IconButton size="small" onClick={() => onOpen(company)} aria-label="open details">
                                <OpenInNewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Box>
        </Card>
    );
}
